import { departureSchema, type MeetingDeparture } from '@/lib/attendance/departure'

export type AttendanceActor = { userId: string; tenantId: string }
type PendingLeave = AttendanceActor & { departure: MeetingDeparture }
const storageKey = 'hikma:pending-meeting-leaves:v1'
const pending = new Map<string, PendingLeave>()
const inFlight = new Map<string, Promise<boolean>>()
const listeners = new Set<() => void>()
let restored = false

function restore() {
  if (restored || typeof window === 'undefined') return
  restored = true
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(storageKey) ?? '[]')
    if (Array.isArray(stored)) for (const item of stored) {
      if (typeof item?.userId === 'string' && typeof item?.tenantId === 'string' &&
          departureSchema.safeParse(item.departure).success) {
        pending.set(item.departure.attendanceId, item)
      }
    }
  } catch { /* Memory delivery still works when browser storage is unavailable. */ }
}
function changed() {
  try { sessionStorage.setItem(storageKey, JSON.stringify([...pending.values()])) } catch {}
  listeners.forEach(listener => listener())
}
export function subscribeMeetingLeaves(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
export function meetingLeaveStatus(attendanceId: string) {
  restore()
  return inFlight.has(attendanceId) ? 'saving' : pending.has(attendanceId) ? 'pending' : 'saved'
}

function send(item: PendingLeave): Promise<boolean> {
  const id = item.departure.attendanceId
  const existing = inFlight.get(id)
  if (existing) return existing
  const request = Promise.resolve().then(async () => {
    try {
      const response = await fetch('/api/attendance/leave', {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item.departure)
      })
      if (!response.ok || !(await response.json()).success) return false
      pending.delete(id)
      return true
    } catch { return false } finally {
      inFlight.delete(id)
      changed()
    }
  })
  inFlight.set(id, request)
  changed()
  return request
}

// Persist before sending; a route change or retry must not lose the departure.
export function finalizeParticipantLeave(actor: AttendanceActor, departure: MeetingDeparture) {
  restore()
  const item = pending.get(departure.attendanceId) ?? { ...actor, departure }
  pending.set(departure.attendanceId, item)
  changed()
  return send(item)
}

export async function flushMeetingLeaves(actor: AttendanceActor): Promise<boolean> {
  restore()
  const items = [...pending.values()].filter(item =>
    item.userId === actor.userId && item.tenantId === actor.tenantId)
  return (await Promise.all(items.map(send))).every(Boolean)
}
